import type { ComponentProps } from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import type { LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "../Button";

export interface DropdownMenuItemHoverAction {
  icon: LucideIcon;
  /** Accessible name for the action button -- pass an already-translated string. */
  label: string;
  onClick: () => void;
  /** Tints the icon and hover background with the destructive color. @default false */
  destructive?: boolean;
}

export interface DropdownMenuItemProps extends ComponentProps<typeof DropdownMenuPrimitive.Item> {
  inset?: boolean;
  variant?: "default" | "destructive";
  /**
   * Shows a trailing action button on hover/focus instead of requiring a separate menu item --
   * generic, not delete-specific: pass any icon/label/handler (delete, archive, pin, duplicate, …).
   */
  hoverAction?: DropdownMenuItemHoverAction;
}

const itemClassName =
  "focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

export const DropdownMenuItem = ({
  className,
  inset,
  variant = "default",
  hoverAction,
  children,
  ...props
}: DropdownMenuItemProps) => {
  if (!hoverAction) {
    return (
      <DropdownMenuPrimitive.Item
        data-slot="dropdown-menu-item"
        data-inset={inset}
        data-variant={variant}
        className={cn(itemClassName, className)}
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Item>
    );
  }

  const { icon: ActionIcon, label, onClick, destructive = false } = hoverAction;

  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(itemClassName, "group", className)}
      {...props}
    >
      <span className="flex-1">{children}</span>
      <Button
        variant="ghost"
        color={destructive ? "destructive" : "muted"}
        size="xs"
        iconOnly
        aria-label={label}
        tabIndex={-1}
        onClick={(event) => {
          // Stops the click from also selecting the menu item underneath.
          event.preventDefault();
          event.stopPropagation();
          onClick();
        }}
        className={cn(
          "pointer-events-none opacity-0 group-focus:pointer-events-auto group-focus:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100",
          // `muted`/`accent` are the same token, so the default ghost hover would be invisible against
          // the item's own focus background -- tint from `foreground` instead, which contrasts against
          // that surface in both themes.
          !destructive && "hover:bg-foreground/10"
        )}
      >
        <ActionIcon />
      </Button>
    </DropdownMenuPrimitive.Item>
  );
};
